import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Overlay,
  Panel,
  Header,
  HeaderTitle,
  HeaderActions,
  ClearButton,
  CloseButton,
  Content,
  EmptyState,
  ComparisonGrid,
  TyreCard,
  TyreHeader,
  RemoveButton,
  BrandLogo,
  TyreName,
  TyreDesign,
  Dimensions,
  SeasonBadge,
  TyreBody,
  InfoSection,
  InfoGrid,
  InfoRow,
  InfoLabel,
  InfoValue,
  PriceDisplay,
  BestPriceBadge,
  Price,
  PriceLabel,
  StockBadge,
  EULabel,
  EULabelItem,
  EULabelValue,
  EULabelLabel,
  LinksRow,
  ExternalLink,
} from './ComparePanel.styled';
import { primary, neutral, season } from '../../style/colors';
import { formatDimensionsCompact } from '../../lib/parseTyreDimensions';
import formatCurrency from '../../lib/formatCurrency';

const getSeasonTranslationKey = (seasonName) => {
  const seasonMap = {
    'summer': 'summer',
    'winter': 'winter',
    'all season': 'allSeason',
    'all-season': 'allSeason',
  };
  return seasonMap[seasonName?.toLowerCase()] || seasonName;
};

const ComparePanel = ({ isOpen, onClose, tyres, onRemoveTyre, onClearAll }) => {
  const { t } = useTranslation();
  
  const getBrandLogoUrl = (brand) => {
    if (!brand || !brand.name) return null;
    try {
      return require(`../../assets/images/${brand.name.toLowerCase()}-tyre.png`);
    } catch {
      return null;
    }
  };

  const getBrandColor = (brand) => {
    if (!brand || !brand.name) return primary[500];
    const brandName = brand.name.toLowerCase();
    if (brandName === 'continental') return primary[500];
    if (brandName === 'barum') return '#E53935';
    if (brandName === 'semperit') return '#43A047';
    return primary[500];
  };

  const getSeasonIcon = (seasonData) => {
    if (!seasonData || !seasonData.name) return 'mdi-weather-cloudy';
    const seasonName = seasonData.name.toLowerCase();
    if (seasonName === 'summer') return 'mdi-white-balance-sunny';
    if (seasonName === 'winter') return 'mdi-snowflake';
    if (seasonName === 'all-season') return 'mdi-weather-partly-cloudy';
    return 'mdi-weather-cloudy';
  };

  const getSeasonColor = (seasonData) => {
    if (!seasonData || !seasonData.name) return neutral[400];
    const seasonName = seasonData.name.toLowerCase();
    if (seasonName === 'summer') return season.summer;
    if (seasonName === 'winter') return season.winter;
    if (seasonName === 'all-season') return neutral[400];
    return neutral[400];
  };

  // Find best/worst values for highlighting
  const getBestPrice = () => {
    if (tyres.length < 2) return null;
    const prices = tyres
      .map(t => parseFloat(t.grossPrice))
      .filter(p => !isNaN(p) && p > 0);
    return prices.length > 0 ? Math.min(...prices) : null;
  };

  const getBestFuelEfficiency = () => {
    if (tyres.length < 2) return null;
    const fuels = tyres
      .map(t => t.rollingResistance)
      .filter(f => f && typeof f === 'string');
    if (fuels.length === 0) return null;
    // Sort A to Z, first is best
    return fuels.sort()[0];
  };

  const getBestWetGrip = () => {
    if (tyres.length < 2) return null;
    const wets = tyres
      .map(t => t.wetPerformance)
      .filter(w => w && typeof w === 'string');
    if (wets.length === 0) return null;
    // Sort A to Z, first is best
    return wets.sort()[0];
  };

  const getBestNoiseLevel = () => {
    if (tyres.length < 2) return null;
    const noises = tyres
      .map(t => {
        if (!t.noiseLevel) return null;
        // Extract numeric part in case "dB" is included
        const match = t.noiseLevel.toString().match(/(\d+)/);
        const val = match ? parseInt(match[0], 10) : NaN;
        return isNaN(val) ? null : val;
      })
      .filter(n => n !== null);
    if (noises.length === 0) return null;
    return Math.min(...noises);
  };

  const bestPrice = getBestPrice();
  const bestFuelEfficiency = getBestFuelEfficiency();
  const bestWetGrip = getBestWetGrip();
  const bestNoiseLevel = getBestNoiseLevel();

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getNumericNoise = (val) => {
     if (!val) return null;
     const match = val.toString().match(/(\d+)/);
     return match ? parseInt(match[0], 10) : null;
  };

  return (
    <>
      <Overlay isOpen={isOpen} onClick={handleOverlayClick} />
      <Panel isOpen={isOpen}>
        <Header>
          <HeaderTitle>
            <i className="mdi mdi-scale-balance" />
            {t('tyreComparison')} ({tyres.length})
          </HeaderTitle>
          <HeaderActions>
            {tyres.length > 0 && (
              <ClearButton onClick={onClearAll}>
                <i className="mdi mdi-delete-sweep" />
                {t('clearAll')}
              </ClearButton>
            )}
            <CloseButton onClick={onClose}>
              <i className="mdi mdi-close" />
            </CloseButton>
          </HeaderActions>
        </Header>

        <Content>
          {tyres.length === 0 ? (
            <EmptyState>
              <i className="mdi mdi-scale-balance" />
              <h3>{t('noTyresToCompare')}</h3>
              <p>
                {t('addTyresToCompare')}
                <br />
                {t('maxCompareInfo')}
              </p>
            </EmptyState>
          ) : (
            <ComparisonGrid columns={tyres.length}>
              {tyres.map((tyre) => {
                const isPriceBest = bestPrice && parseFloat(tyre.grossPrice) === bestPrice;
                const isFuelBest = bestFuelEfficiency && tyre.rollingResistance === bestFuelEfficiency;
                const isWetGripBest = bestWetGrip && tyre.wetPerformance === bestWetGrip;
                const isNoiseBest = bestNoiseLevel !== null && getNumericNoise(tyre.noiseLevel) === bestNoiseLevel;

                return (
                  <TyreCard key={tyre._id} brandColor={getBrandColor(tyre.brand)}>
                    <TyreHeader>
                      <RemoveButton
                        onClick={() => onRemoveTyre(tyre._id)}
                        title={t('removeFromComparison')}
                      >
                        <i className="mdi mdi-close" />
                      </RemoveButton>
                      {tyre.brand && getBrandLogoUrl(tyre.brand) && (
                        <BrandLogo src={getBrandLogoUrl(tyre.brand)} alt={tyre.brand.name} />
                      )}
                      <TyreName>{tyre.name}</TyreName>
                      {tyre.design && <TyreDesign>{tyre.design}</TyreDesign>}
                      <Dimensions>
                        {formatDimensionsCompact(tyre.dimensions) || tyre.dimensions}
                      </Dimensions>
                      {tyre.season && (
                        <div style={{ textAlign: 'center' }}>
                          <SeasonBadge>
                            <i className={`mdi ${getSeasonIcon(tyre.season)}`} style={{ color: getSeasonColor(tyre.season) }} />
                            {t(getSeasonTranslationKey(tyre.season.name))}
                          </SeasonBadge>
                        </div>
                      )}
                    </TyreHeader>

                    <TyreBody>
                      {tyre.grossPrice && (
                        <PriceDisplay isBest={isPriceBest}>
                          {isPriceBest && <BestPriceBadge>{t('bestPrice')}</BestPriceBadge>}
                          <Price>{formatCurrency(tyre.grossPrice)}</Price>
                          <PriceLabel isBest={isPriceBest}>{t('price')}</PriceLabel>
                        </PriceDisplay>
                      )}

                      {tyre.quantity !== undefined && (
                        <StockBadge inStock={tyre.quantity > 0}>
                          <span>
                            {tyre.quantity > 0
                              ? `${t('inStockSimple')}: ${tyre.quantity}`
                              : t('notInStock')}
                          </span>
                          <i
                            className={`mdi ${
                              tyre.quantity > 0 ? 'mdi-check-circle' : 'mdi-alert-circle'
                            }`}
                          />
                        </StockBadge>
                      )}

                      <InfoGrid>
                        <InfoRow>
                          <InfoLabel>{t('catalog')}</InfoLabel>
                          <InfoValue>{tyre.catalogNumber}</InfoValue>
                        </InfoRow>
                        {tyre.loadRating && (
                          <InfoRow>
                            <InfoLabel>{t('loadRating')}</InfoLabel>
                            <InfoValue>{tyre.loadRating}</InfoValue>
                          </InfoRow>
                        )}
                      </InfoGrid>

                      {(tyre.rollingResistance || tyre.wetPerformance || tyre.noiseLevel) && (
                        <InfoSection>
                          <EULabel>
                            {tyre.rollingResistance && (
                              <EULabelItem isBest={isFuelBest}>
                                <i className="mdi mdi-fuel" />
                                <EULabelValue>{tyre.rollingResistance}</EULabelValue>
                                <EULabelLabel isBest={isFuelBest}>{t('fuel')}</EULabelLabel>
                              </EULabelItem>
                            )}
                            {tyre.wetPerformance && (
                              <EULabelItem isBest={isWetGripBest}>
                                <i className="mdi mdi-water" />
                                <EULabelValue>{tyre.wetPerformance}</EULabelValue>
                                <EULabelLabel isBest={isWetGripBest}>{t('grip')}</EULabelLabel>
                              </EULabelItem>
                            )}
                            {tyre.noiseLevel && (
                              <EULabelItem isBest={isNoiseBest}>
                                <i className="mdi mdi-volume-high" />
                                <EULabelValue>{tyre.noiseLevel}</EULabelValue>
                                <EULabelLabel isBest={isNoiseBest}>{t('noise')}</EULabelLabel>
                              </EULabelItem>
                            )}
                          </EULabel>
                        </InfoSection>
                      )}

                      {(tyre.efficiencySticker || tyre.erpel) && (
                        <LinksRow>
                          {tyre.efficiencySticker && (
                            <ExternalLink
                              href={tyre.efficiencySticker}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={t('energyLabel')}
                            >
                              <i className="mdi mdi-label" />
                            </ExternalLink>
                          )}
                          {tyre.erpel && (
                            <ExternalLink
                              href={tyre.erpel}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="EPREL"
                            >
                              <i className="mdi mdi-qrcode" />
                            </ExternalLink>
                          )}
                        </LinksRow>
                      )}
                    </TyreBody>
                  </TyreCard>
                );
              })}
            </ComparisonGrid>
          )}
        </Content>
      </Panel>
    </>
  );
};

ComparePanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tyres: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRemoveTyre: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
};

export default ComparePanel;
